<template>
  <div>
    <!-- List Table -->
    <List
      :header="header"
      :data="data"
      :pagination="pagination"
      :loading="loading"
      @updatePage="updatePage"
      :moduleName="`servers.storage`"
      :moduleCreateEvent="`subHeaderDetailOpen`"
      :moduleSearch="search"
    >
      <template v-slot:item.icon="{ item }">
        <i :class="`icon2 ${item.is_attached ? 'fas' : 'far'} fa-hdd`"></i>
      </template>

      <template v-slot:item.name="{ item }">
        <div class="name">
          {{ item.name }}
          <div v-if="item.is_attached">
            <p
              v-if="
                item.virtualMachines && item.virtualMachines.data.length > 0
              "
            >
              {{ $t('servers.attachedTo') }}
              <router-link
                class="link"
                :to="`/compute/virtual-machines/${item.virtualMachines.data[0].id}`"
                >{{ item.virtualMachines.data[0].name }}</router-link
              >
            </p>
            <p v-else>{{ $t('servers.attachedNotFound') }}</p>
          </div>
          <p v-else>{{ $t('servers.notAttached') }}</p>
        </div>
      </template>

      <template v-slot:item.is_attached="{ item }">
        {{ $t(`global.${item.is_attached ? 'yes' : 'no'}`) }}
      </template>

      <template v-slot:item.is_active="{ item }">
        {{ $t(`global.${item.is_active ? 'yes' : 'no'}`) }}
      </template>

      <template v-slot:item.action="{ item }">
        <b-dropdown
          class="actions-button nomaxh"
          variant="dark"
          :text="$t('global.actions')"
        >
          <b-dropdown-item
            @click.native="openRenameDiskPopup(item)"
            v-if="item.available_operations.includes('rename')"
            >{{ $t('servers.renameDisk') }}</b-dropdown-item
          >
          <b-dropdown-item
            v-if="!item.is_attached"
            @click.native="openAttachToServer(item)"
            >{{ $t('servers.attachToAServer') }}</b-dropdown-item
          >
          <b-dropdown-item
            v-if="item.is_attached"
            @click.native="openDetachFromServer(item)"
            >{{ $t('servers.detachFromServer') }}</b-dropdown-item
          >
          <b-dropdown-item
            @click.native="openChangeDiskSizePopup(item)"
            v-if="item.available_operations.includes('resize')"
            >{{ $t('servers.changeDiskSize') }}</b-dropdown-item
          >
          <b-dropdown-item
            @click.native="openCloneDiskPopup(item)"
            v-if="item.available_operations.includes('clone')"
            >{{ $t('servers.cloneDisk') }}</b-dropdown-item
          >
          <b-dropdown-divider></b-dropdown-divider>
          <b-dropdown-item
            class="dropdownAlertLabel"
            @click.native="openTerminateDiskPopup(item)"
            v-if="item.available_operations.includes('destroy')"
            >{{ $t('servers.terminate') }}</b-dropdown-item
          >
        </b-dropdown>
      </template>
    </List>

    <!-- Create Popup -->
    <DetailModal
      :title="
        $t(dynamicOperationComponentTitle ? dynamicOperationComponentTitle : '')
      "
      :show="showDetail"
      @confirm="confirmModal()"
      @closeDetail="closeDetail()"
      :resetForm="resetForm"
    >
      <component
        v-if="dynamicOperationComponent != ''"
        :is="dynamicOperationComponent"
        :confirmModal="confirmCreate"
        @resetDetailForm="resetDetailForm"
        :selectedId="selected.id"
        :selectedName="selected.name"
        :selectedTotalDisk="selected.total_disk"
        :selectedDiskFreeSize="
          selected.expansion_metrics ? selected.expansion_metrics.free : 0
        "
        :virtualMachines="
          selected.virtualMachines ? selected.virtualMachines.data : []
        "
      />

      <TabMenu v-if="dynamicOperationComponent == '' && showDetail">
        <TabItem name="servers.newVD" :selected="true">
          <VDCreate
            :confirmModal="confirmCreate"
            @resetDetailForm="resetDetailForm"
          />
        </TabItem>
        <TabItem name="forms.vd.cloneFromTemplate">
          <VDCloneTemplate
            :confirmModal="confirmCreate"
            @resetDetailForm="resetDetailForm"
          />
        </TabItem>
      </TabMenu>
    </DetailModal>

    <DeleteOperation :url="deleteOperationUrl" @updateList="getList" />
  </div>
</template>

<script>
// Utils
import { SET_BREADCRUMB } from '@/core/services/store/breadcrumbs.module'
import { SET_CUSTOMHEADERPARAMS } from '@/core/services/store/customsubheader.module'
import EventBus from '@/event-bus'

// Services
import ApiService from '@/core/services/api.service'

export default {
  name: 'VirtualDisks',
  data() {
    return {
      search: '',
      filters: '',
      showDetail: false,
      loading: false,
      confirmCreate: false,
      resetForm: false,
      header: [
        { text: '', value: 'icon', size: '100px' },
        { text: 'global.name', value: 'name', size: '2fr' },
        { text: 'global.diskType', value: 'disk_type' },
        { text: 'global.sizeMb', value: 'total_disk' },
        { text: 'global.isAttached', value: 'is_attached' },
        { text: 'global.isActive', value: 'is_active' },
        { text: 'global.actions', value: 'action' },
      ],
      data: [],
      pagination: {},
      meta: {},
      deleteOperationUrl: '',
      dynamicOperationComponentTitle: '',
      dynamicOperationComponent: '',
      selected: '',
    }
  },
  created() {
    this.getList()
  },
  mounted() {
    this.$store.dispatch(SET_BREADCRUMB, [
      {
        title: 'servers.vd',
        route: '/storage/virtual-disks',
      },
    ])
    this.$store.dispatch(SET_CUSTOMHEADERPARAMS, {
      menuList: [
        {
          title: 'servers.storage',
        },
        {
          title: 'servers.vd',
          route: '/storage/virtual-disks',
        },
      ],
      searchPlaceholder: 'servers.searchForVD',
      detailPlaceholder: 'servers.newVD',
    })

    EventBus.$on(`subHeaderSearch`, this.listenersubHeaderSearch)
    EventBus.$on(`subHeaderDetailOpen`, this.listenersubHeaderDetailOpen)
  },
  beforeDestroy() {
    EventBus.$off(`subHeaderSearch`, this.listenersubHeaderSearch)
    EventBus.$off(`subHeaderDetailOpen`, this.listenersubHeaderDetailOpen)
  },
  methods: {
    listenersubHeaderSearch(payload) {
      this.search = payload
      this.getList()
    },
    listenersubHeaderDetailOpen(payload) {
      this.dynamicOperationComponentTitle = 'servers.newVD'
      this.dynamicOperationComponent = ''
      this.showDetail = payload
    },
    getList() {
      this.loading = true

      const { current_page } = this.meta

      let $query = `iaas/virtual-disk-images?include=virtualMachines&position=id|desc&page=${
        current_page ? current_page : 1
      }`

      if (this.search) $query += `&name=${this.search}`

      ApiService.get($query)
        .then(({ data }) => {
          this.data = data.data
          this.pagination = data.meta ? data.meta.pagination : {}

          this.loading = false
        })
        .catch((err) => {
          console.log(err)
          this.data = []
          this.loading = false
        })
    },
    updatePage(value) {
      this.meta = value

      window.scrollTo({
        top: 0,
        behavior: 'smooth',
      })
    },
    closeDetail() {
      this.showDetail = false
    },
    operations(item) {
      this.$generateToast(this, 'info', 'İşlem eklenecek')
    },
    confirmModal() {
      this.confirmCreate = true
      setTimeout(() => {
        this.confirmCreate = false
      }, 200)
    },
    resetDetailForm(closeDetail = false) {
      if (closeDetail) {
        this.closeDetail()
        this.getList()
      }

      this.resetForm = true
      setTimeout(() => {
        this.resetForm = false
      }, 200)
    },
    openRenameDiskPopup(item) {
      this.dynamicOperationComponentTitle = 'servers.renameDisk'
      this.dynamicOperationComponent = 'RenameDisk'
      this.showDetail = true
      this.selected = item
    },
    openAttachToServer(item) {
      this.dynamicOperationComponentTitle = 'servers.attachToAServer'
      this.dynamicOperationComponent = 'AttachToAServer'
      this.showDetail = true
      this.selected = item
    },
    openDetachFromServer(item) {
      this.dynamicOperationComponentTitle = 'servers.detachFromServer'
      this.dynamicOperationComponent = 'DetachFromServer'
      this.showDetail = true
      this.selected = item
    },
    openChangeDiskSizePopup(item) {
      this.dynamicOperationComponentTitle = 'servers.changeDiskSize'
      this.dynamicOperationComponent = 'ChangeDiskSize'
      this.showDetail = true
      this.selected = item
    },
    openCloneDiskPopup(item) {
      this.dynamicOperationComponentTitle = 'servers.cloneDisk'
      this.dynamicOperationComponent = 'CloneDisk'
      this.showDetail = true
      this.selected = item
    },
    openTerminateDiskPopup(item) {
      this.deleteOperationUrl = `iaas/virtual-disk-images/${item.id}`
      this.$bvModal.show('modal-deleteOperation')
    },
  },
  watch: {
    meta: {
      handler() {
        this.getList()
      },
      deep: true,
    },
  },
  components: {
    //Tools
    RenameDisk: () =>
      import('@/components/compute/virtualMachines/diskImages/RenameDisk'),
    AttachToAServer: () =>
      import('@/components/compute/virtualMachines/diskImages/AttachToAServer'),
    DetachFromServer: () =>
      import(
        '@/components/compute/virtualMachines/diskImages/DetachFromServer'
      ),
    ChangeDiskSize: () =>
      import('@/components/compute/virtualMachines/diskImages/ChangeDiskSize'),
    CloneDisk: () =>
      import('@/components/compute/virtualMachines/diskImages/CloneDisk'),
    CreateAttachDiskImages: () =>
      import(
        '@/components/compute/virtualMachines/diskImages/CreateAttachDiskImages'
      ),
    AttachExistingDiskImages: () =>
      import(
        '@/components/compute/virtualMachines/diskImages/AttachExistingDiskImages'
      ),

    //Forms
    VDCreate: () => import('@/components/forms/VDCreate'),
    VDCloneTemplate: () => import('@/components/forms/VDCloneTemplate'),
  },
}
</script>
